import { Controller } from "@hotwired/stimulus"

// tail -f for the log console: re-fetches the frame on an interval and keeps the
// view pinned to the newest line.
//
// The URL is rebuilt from the filter form on every refresh rather than captured
// once, so following keeps whatever file, level and search are on screen — and
// changing one of them while it runs simply takes effect on the next tick.
//
// Polling stops while the tab is in the background. A log console left open on a
// second monitor during a lanparty would otherwise keep asking for a megabyte of
// log that nobody is looking at.
export default class extends Controller {
    static targets = ["form", "body", "toggle", "pulse"]

    static values = {
        url: String,
        interval: { type: Number, default: 3000 },
    }

    connect() {
        // Newest lines are at the bottom, and the bottom is what someone opening
        // a log came for.
        this.scrollToEnd()

        this.onFrameRender = () => this.scrollToEnd()
        this.onVisibilityChange = () => this.syncTimer()

        this.element.addEventListener("turbo:frame-render", this.onFrameRender)
        document.addEventListener("visibilitychange", this.onVisibilityChange)
    }

    disconnect() {
        this.stop()
        this.element.removeEventListener("turbo:frame-render", this.onFrameRender)
        document.removeEventListener("visibilitychange", this.onVisibilityChange)
    }

    toggle() {
        this.pulseTarget.hidden = !this.following
        this.syncTimer()
        // Switching it on should show the newest line now, not in three seconds.
        if (this.following) this.refresh()
    }

    refresh() {
        const frame = this.frame
        if (!frame) return

        const url = this.currentUrl
        // Assigning the same src is not a navigation, so ask outright.
        if (frame.src === url) frame.reload()
        else frame.src = url
    }

    syncTimer() {
        this.stop()
        if (this.following && !document.hidden) {
            this.timer = setInterval(() => this.refresh(), this.intervalValue)
        }
    }

    stop() {
        clearInterval(this.timer)
        this.timer = null
    }

    scrollToEnd() {
        if (this.hasBodyTarget) this.bodyTarget.scrollTop = this.bodyTarget.scrollHeight
    }

    get following() {
        return this.hasToggleTarget && this.toggleTarget.checked
    }

    get frame() {
        return this.element.querySelector("turbo-frame#console-log")
    }

    get currentUrl() {
        const url = new URL(this.urlValue, window.location.origin)

        new FormData(this.formTarget).forEach((value, key) => {
            if (value !== "") url.searchParams.set(key, value)
        })

        return url.toString()
    }
}
